import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Search, RefreshCw, AlertCircle, Edit2, Calendar, 
  ChevronLeft, ChevronRight, SlidersHorizontal, Download, 
  FileText, FileSpreadsheet, X
} from 'lucide-react';
import api from '../../api';
import moment from 'moment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

// Format time
const formatTime = (timeStr) => {
  if (!timeStr) return '--:--';
  return moment(timeStr, ['HH:mm', 'H:mm']).format('hh:mm A');
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  return moment(dateStr).format('DD MMM YYYY');
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '--';
  return moment(dateStr).format('DD MMM');
};

const formatDateForFile = (dateStr) => {
  if (!dateStr) return '--';
  return moment(dateStr).format('DD-MM-YYYY');
};

// Profile photo helper
function getStudentProfileImage(details) {
  const backendUrl = api?.defaults?.baseURL;
  const profileUrl = details?.profilePhotoUrl || details?.profilePhoto || details?.photo;
  const name = details?.name || 'Student';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');
  const imgSrc = showImage ? (profileUrl.startsWith('http') ? profileUrl : `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`) : null;
  return { imgSrc, initials };
}

// Get status display
const getStatusDisplay = (status) => {
  const map = {
    present: { label: 'Present', short: 'P', color: 'emerald' },
    absent: { label: 'Absent', short: 'A', color: 'rose' },
    late: { label: 'Late', short: 'L', color: 'amber' },
    'on-duty': { label: 'On Duty', short: 'OD', color: 'sky' },
    'on_duty': { label: 'On Duty', short: 'OD', color: 'sky' },
    medical: { label: 'Medical', short: 'M', color: 'purple' },
    excused: { label: 'Excused', short: 'E', color: 'indigo' }
  };
  return map[status] || { label: '—', short: '—', color: 'slate' };
};

// Ultra-premium micro options selector for immediate actions
const MicroStatusSelector = ({ value, onChange, onBlur }) => (
  <div className="absolute inset-0 bg-white z-30 flex items-center justify-center p-0.5 animate-in fade-in duration-100">
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      autoFocus
      className="w-full h-full text-center bg-slate-900 text-white font-mono text-[11px] font-bold rounded border-none focus:ring-2 focus:ring-indigo-500 cursor-pointer appearance-none px-1"
    >
      <option value="" className="bg-white text-slate-900">—</option>
      <option value="present" className="bg-white text-slate-900">P</option>
      <option value="absent" className="bg-white text-slate-900">A</option>
      <option value="late" className="bg-white text-slate-900">L</option>
      <option value="on-duty" className="bg-white text-slate-900">OD</option>
      <option value="medical" className="bg-white text-slate-900">M</option>
    </select>
  </div>
);

// High-Density Matrix Cell Component
const AttendanceCell = React.memo(({ status, canEdit, sessionId, studentId, onEdit, onMarkAttendance, session, isMobileView }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(status);

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  const isMarkingAllowed = useMemo(() => {
    if (!onMarkAttendance || !session) return false;
    const now = moment();
    const sessionDateTime = moment(session.sessionDate);
    const [startHour, startMinute] = (session.startTime || '00:00').split(':');
    sessionDateTime.set({ hour: parseInt(startHour), minute: parseInt(startMinute), second: 0 });
    return now.isSameOrAfter(sessionDateTime);
  }, [session, onMarkAttendance]);

  const handleStatusChange = async (newStatus) => {
    if (!canEdit || updating) return;
    
    setUpdating(true);
    setCurrentStatus(newStatus);
    
    try {
      await api.post(`/api/faculty/attendance/session/${sessionId}/mark`, {
        records: [{ studentId, status: newStatus }]
      });
      if (onEdit) onEdit();
    } catch (error) {
      console.error('Error updating attendance:', error);
      setCurrentStatus(status);
    } finally {
      setUpdating(false);
      setIsEditing(false);
    }
  };

  const currentStyle = getStatusDisplay(currentStatus);

  if (updating) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-50/30">
        <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />
      </div>
    );
  }

  if (canEdit && !isEditing && onMarkAttendance && session && isMarkingAllowed && !currentStatus) {
    return (
      <div className="w-full h-full flex items-center justify-center p-1">
        <button
          onClick={() => onMarkAttendance(session)}
          className="px-2 py-1 text-[10px] font-bold tracking-tight uppercase rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white transition-all w-full"
        >
          Mark
        </button>
      </div>
    );
  }

  if (isMobileView) {
    return (
      <div className="relative w-12 h-8 flex items-center justify-center">
        {canEdit && isEditing ? (
          <MicroStatusSelector value={currentStatus} onChange={handleStatusChange} onBlur={() => setIsEditing(false)} />
        ) : (
          <button
            disabled={!canEdit}
            onClick={() => setIsEditing(true)}
            className={`w-full h-full text-xs rounded border transition-all flex items-center justify-center bg-${currentStyle.color}-500/10 text-${currentStyle.color}-700 font-bold border-${currentStyle.color}-500/20 ${canEdit ? 'active:scale-95 hover:bg-slate-50' : 'cursor-default'}`}
          >
            {currentStyle.short}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full relative flex items-center justify-center border-b border-r border-slate-100 group-hover:bg-slate-50/40">
      {canEdit && isEditing ? (
        <MicroStatusSelector value={currentStatus} onChange={handleStatusChange} onBlur={() => setIsEditing(false)} />
      ) : (
        <button
          disabled={!canEdit}
          onClick={() => setIsEditing(true)}
          className={`w-full h-full text-xs font-mono transition-all flex items-center justify-center bg-${currentStyle.color}-500/10 text-${currentStyle.color}-700 font-bold border-${currentStyle.color}-500/20 ${canEdit ? 'hover:bg-indigo-50 hover:text-indigo-600' : 'cursor-default'}`}
        >
          {currentStyle.short}
        </button>
      )}
    </div>
  );
});
AttendanceCell.displayName = 'AttendanceCell';

// ============================================================
// DOWNLOAD MODAL COMPONENT
// ============================================================
const DownloadModal = ({ isOpen, onClose, onDownload, sessions, students, subject, classItem, facultyName, tenantInfo }) => {
  const [format, setFormat] = useState('pdf');
  const [dateRange, setDateRange] = useState('full');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeSignatures, setIncludeSignatures] = useState(false);
  const [signatories, setSignatories] = useState([
    { id: 1, designation: 'Class Teacher', name: '', date: new Date().toISOString().split('T')[0] }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate available date range
  const availableDates = useMemo(() => {
    if (!sessions || sessions.length === 0) return { min: null, max: null };
    const dates = sessions.map(s => new Date(s.sessionDate));
    return {
      min: new Date(Math.min(...dates)),
      max: new Date(Math.max(...dates))
    };
  }, [sessions]);

  // Set default date range
  useEffect(() => {
    if (availableDates.min && availableDates.max) {
      setFromDate(moment(availableDates.min).format('YYYY-MM-DD'));
      setToDate(moment(availableDates.max).format('YYYY-MM-DD'));
    }
  }, [availableDates]);

  const handleAddSignatory = () => {
    const newId = Math.max(...signatories.map(s => s.id)) + 1;
    setSignatories([...signatories, { 
      id: newId, 
      designation: '', 
      name: '', 
      date: new Date().toISOString().split('T')[0] 
    }]);
  };

  const handleRemoveSignatory = (id) => {
    if (signatories.length <= 1) return;
    setSignatories(signatories.filter(s => s.id !== id));
  };

  const handleSignatoryChange = (id, field, value) => {
    setSignatories(signatories.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const handleDownload = async () => {
    setLoading(true);
    setError('');

    try {
      // Filter sessions based on date range
      let filteredSessions = [...sessions];
      
      if (dateRange === 'custom') {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        
        filteredSessions = filteredSessions.filter(s => {
          const sessionDate = new Date(s.sessionDate);
          return sessionDate >= from && sessionDate <= to;
        });
      }

      if (filteredSessions.length === 0) {
        setError('No sessions found in the selected date range.');
        setLoading(false);
        return;
      }

      // Prepare data for export
      const exportData = {
        format,
        sessions: filteredSessions,
        students: students || [],
        subject,
        classItem,
        facultyName: facultyName || 'Faculty',
        tenantInfo: tenantInfo || {},
        dateRange: dateRange === 'full' ? 'Full Attendance' : `${formatDateForFile(fromDate)} to ${formatDateForFile(toDate)}`,
        includeSignatures,
        signatories: includeSignatures ? signatories : [],
        generatedAt: new Date().toISOString()
      };

      await onDownload(exportData);
      onClose();
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to generate download');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Download className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Export Attendance</h2>
              <p className="text-xs text-slate-500">{subject?.name} • {classItem?.className}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Step 1: Format Selection */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Step 1: Select Export Format
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setFormat('pdf')}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  format === 'pdf' 
                    ? 'border-indigo-600 bg-indigo-50 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileText className={`w-6 h-6 mx-auto mb-2 ${format === 'pdf' ? 'text-indigo-600' : 'text-slate-400'}`} />
                <p className={`text-sm font-bold ${format === 'pdf' ? 'text-indigo-600' : 'text-slate-700'}`}>PDF</p>
                <p className="text-[10px] text-slate-400">Professional formatted report</p>
              </button>
              <button
                onClick={() => setFormat('excel')}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  format === 'excel' 
                    ? 'border-emerald-600 bg-emerald-50 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileSpreadsheet className={`w-6 h-6 mx-auto mb-2 ${format === 'excel' ? 'text-emerald-600' : 'text-slate-400'}`} />
                <p className={`text-sm font-bold ${format === 'excel' ? 'text-emerald-600' : 'text-slate-700'}`}>Excel</p>
                <p className="text-[10px] text-slate-400">Spreadsheet with filters</p>
              </button>
            </div>
          </div>

          {/* Step 2: Date Range */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Step 2: Select Date Range
            </label>
            <div className="space-y-3">
              <div className="flex gap-3">
                <button
                  onClick={() => setDateRange('full')}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-all ${
                    dateRange === 'full' 
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-600' 
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Full Attendance
                </button>
                <button
                  onClick={() => setDateRange('custom')}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-all ${
                    dateRange === 'custom' 
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-600' 
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Custom Range
                </button>
              </div>

              {dateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Signatures */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-bold text-slate-700">
                Step 3: Signature Fields
              </label>
              <button
                onClick={() => setIncludeSignatures(!includeSignatures)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  includeSignatures ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  includeSignatures ? 'translate-x-5' : ''
                }`} />
              </button>
            </div>

            {includeSignatures && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                {signatories.map((signatory) => (
                  <div key={signatory.id} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Designation</label>
                        <input
                          type="text"
                          value={signatory.designation}
                          onChange={(e) => handleSignatoryChange(signatory.id, 'designation', e.target.value)}
                          placeholder="e.g., HOD, Principal"
                          className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Name (Optional)</label>
                        <input
                          type="text"
                          value={signatory.name}
                          onChange={(e) => handleSignatoryChange(signatory.id, 'name', e.target.value)}
                          placeholder="Full name"
                          className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveSignatory(signatory.id)}
                      disabled={signatories.length <= 1}
                      className="mt-4 p-1 text-rose-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddSignatory}
                  className="w-full py-2 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  + Add Signatory
                </button>
              </div>
            )}
          </div>

          {/* Preview Summary */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs font-bold text-slate-700">Export Summary</p>
            <div className="mt-1.5 grid grid-cols-2 gap-1 text-[11px] text-slate-600">
              <span>Format: <span className="font-bold uppercase">{format}</span></span>
              <span>Date Range: <span className="font-bold">{dateRange === 'full' ? 'Full' : `${formatDateForFile(fromDate)} to ${formatDateForFile(toDate)}`}</span></span>
              <span>Sessions: <span className="font-bold">{dateRange === 'full' ? sessions.length : 'Filtered'}</span></span>
              <span>Signatures: <span className="font-bold">{includeSignatures ? signatories.length : 'None'}</span></span>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-600 text-xs font-medium">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-slate-200">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={loading}
              className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate & Download
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN ATTENDANCE MATRIX VIEW COMPONENT
// ============================================================
const AttendanceMatrixView = ({ facultyId, classItem, subject, labBatch, onRefresh, isActive, onMarkAttendance, attendanceRefreshToken }) => {
  const [sessions, setSessions] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [tenantInfo, setTenantInfo] = useState({
    name: '',
    logoUrl: '',
    address: '',
    phone: '',
    email: ''
  });
  
  const [selectedMobileSessionIdx, setSelectedMobileSessionIdx] = useState(0);
  const loadInProgress = useRef(false);

  // Fetch tenant info
  const fetchTenantInfo = useCallback(async () => {
    try {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) return;
      
      const response = await api.get(`/api/organization/basic-info/${tenantId}`);
      if (response.data?.success) {
        setTenantInfo({
          name: response.data.name || '',
          logoUrl: response.data.logoUrl || '',
          address: response.data.address || '',
          phone: response.data.phone || '',
          email: response.data.email || ''
        });
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
      try {
        const t = JSON.parse(localStorage.getItem('tenantData') || '{}');
        if (t.institutionName) {
          setTenantInfo({
            name: t.institutionName || '',
            logoUrl: t.logoUrl || '',
            address: t.address || '',
            phone: t.phone || '',
            email: t.email || ''
          });
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
    }
  }, []);

  const canSessionBeMarked = useCallback((session) => {
    if (!session) return false;
    const isMarkedSession = session.attendanceMarkingStatus === 'completed';
    const isBlockedSession = session.isLocked === true || session.isBlocked === true;
    return (
      session.sessionStatus?.canMark === true ||
      session.sessionStatus?.canEdit === true ||
      session.canMarkAttendance === true ||
      session.canMark === true ||
      session.isUnblockedByAdmin === true ||
      (!isMarkedSession && !isBlockedSession)
    );
  }, []);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    try {
      const classId = classItem?.classId || classItem?._id;
      const subjectId = subject?.subjectId || subject?._id;
      
      if (!classId || !subjectId) return [];
      
      let url = `/api/faculty/classes/${classId}/subjects/${subjectId}/students`;
      const params = new URLSearchParams();
      
      if (subject?.isLab && labBatch?.batchId) params.append('batchId', labBatch.batchId);
      if (subject?.electiveGroupId) params.append('electiveGroupId', subject.electiveGroupId);
      if (subject?.subGroupId) params.append('subGroupId', subject.subGroupId);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      if (response.data?.success) return response.data.data.students?.list || [];
      return [];
    } catch (err) {
      console.error('Error fetching students:', err);
      return [];
    }
  }, [classItem, subject, labBatch]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const classId = classItem?.classId || classItem?._id;
      const subjectId = subject?.subjectId || subject?._id;
      
      if (!classId || !subjectId) return [];
      
      const params = new URLSearchParams();
      params.append('classId', classId);
      params.append('subjectId', subjectId);
      
      if (labBatch?.batchId) params.append('labBatchId', labBatch.batchId);
      if (subject?.electiveGroupId) params.append('electiveGroupId', subject.electiveGroupId);
      if (subject?.subGroupId) params.append('subGroupId', subject.subGroupId);
      
      const response = await api.get(`/api/faculty/${facultyId}/attendance/all-with-status?${params}`);
      
      if (response.data.success) {
        let allSessions = response.data.data.sessions || [];

        allSessions = allSessions.filter(session => {
          const sessionClassId = session.classId?._id || session.classId;
          const sessionSubjectId = session.subjectId?._id || session.subjectId;
          const classMatch = sessionClassId?.toString() === (classItem?.classId || classItem?._id)?.toString();
          const subjectMatch = sessionSubjectId?.toString() === (subject?.subjectId || subject?._id)?.toString();
          
          let batchMatch = true;
          if (labBatch?.batchId && session.labBatchId) {
            batchMatch = session.labBatchId?.toString() === labBatch.batchId?.toString();
          }
          
          return classMatch && subjectMatch && batchMatch;
        });

        return [...allSessions].sort((a, b) => new Date(a.sessionDate) - new Date(b.sessionDate));
      }
      return [];
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.response?.data?.message || 'Failed to load attendance data');
      return [];
    }
  }, [facultyId, classItem, subject, labBatch]);

  const buildAttendanceMap = useCallback((sessionsData, studentsData) => {
    const map = {};
    
    studentsData.forEach(student => {
      const studentId = student.studentId || student._id;
      map[studentId] = {
        details: student,
        records: {},
        summary: { total: 0, present: 0, absent: 0, late: 0, onDuty: 0, medical: 0, effectivePresent: 0, percentage: 0 }
      };
    });
    
    sessionsData.forEach(session => {
      if (session.attendance?.length) {
        session.attendance.forEach(record => {
          const studentId = record.studentId?._id || record.studentId;
          if (map[studentId]) {
            const canEdit = session.sessionStatus?.canEdit === true || 
                           session.sessionStatus?.canMark === true || 
                           session.canMarkAttendance === true;
            map[studentId].records[session._id] = {
              status: record.status,
              canEdit,
              isLocked: session.isLocked,
              sessionDate: session.sessionDate,
              startTime: session.startTime
            };
          }
        });
      }
    });
    
    Object.keys(map).forEach(studentId => {
      const student = map[studentId];
      const records = Object.values(student.records);
      
      student.summary.present = records.filter(r => r?.status === 'present').length;
      student.summary.absent = records.filter(r => r?.status === 'absent').length;
      student.summary.late = records.filter(r => r?.status === 'late').length;
      student.summary.onDuty = records.filter(r => r?.status === 'on-duty').length;
      student.summary.medical = records.filter(r => r?.status === 'medical').length;
      
      student.summary.total = records.filter(r => r && r.status).length;
      student.summary.effectivePresent = student.summary.present + student.summary.onDuty + student.summary.medical;
      
      student.summary.percentage = student.summary.total > 0 
        ? Math.round((student.summary.effectivePresent / student.summary.total) * 100)
        : 0;
    });
    
    return map;
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!isActive || !facultyId || !classItem || !subject || loadInProgress.current) return;
    
    loadInProgress.current = true;
    setLoading(true);
    setError('');
    
    try {
      const [studentsData, sessionsData] = await Promise.all([fetchStudents(), fetchSessions()]);
      const map = buildAttendanceMap(sessionsData, studentsData);
      setAttendanceMap(map);
      setSessions(sessionsData);
      
      if(sessionsData.length > 0) {
        setSelectedMobileSessionIdx(sessionsData.length - 1);
      }
    } catch (err) {
      console.error('Error loading matrix:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
      loadInProgress.current = false;
    }
  }, [fetchStudents, fetchSessions, buildAttendanceMap, isActive, facultyId, classItem, subject]);

  // Load tenant info
  useEffect(() => {
    fetchTenantInfo();
  }, [fetchTenantInfo]);

  useEffect(() => {
    if (isActive && facultyId && classItem && subject) loadData();
  }, [isActive, facultyId, classItem, subject, labBatch, attendanceRefreshToken, loadData]);

  const handleCellEdit = useCallback(() => {
    loadData().finally(() => onRefresh?.());
  }, [loadData, onRefresh]);

  // Filter students
  const filteredStudents = useMemo(() => {
    let filtered = Object.values(attendanceMap);
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.details?.name?.toLowerCase().includes(term) ||
        s.details?.USN?.toLowerCase().includes(term)
      );
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(s => {
        const pct = s.summary.percentage;
        if (filterType === 'critical') return pct < 75;
        if (filterType === 'warning') return pct >= 75 && pct < 85;
        if (filterType === 'good') return pct >= 85 && pct < 95;
        if (filterType === 'excellent') return pct >= 95;
        return true;
      });
    }
    
    filtered.sort((a, b) => {
      const aVal = a.details?.USN || a.details?.rollNumber || '';
      const bVal = b.details?.USN || b.details?.rollNumber || '';
      return aVal.localeCompare(bVal);
    });
    
    return filtered;
  }, [attendanceMap, searchTerm, filterType]);

  // Stats
  const stats = useMemo(() => {
    const total = filteredStudents.length;
    const avg = total > 0 ? Math.round(filteredStudents.reduce((sum, s) => sum + s.summary.percentage, 0) / total) : 0;
    const below75 = filteredStudents.filter(s => s.summary.percentage < 75).length;
    const above90 = filteredStudents.filter(s => s.summary.percentage >= 90).length;
    return { total, avg, below75, above90, totalSessions: sessions.length, totalStudents: Object.values(attendanceMap).length };
  }, [filteredStudents, sessions.length, attendanceMap]);

  const handleMobileSessionStep = (direction) => {
    if (direction === 'prev' && selectedMobileSessionIdx > 0) {
      setSelectedMobileSessionIdx(prev => prev - 1);
    } else if (direction === 'next' && selectedMobileSessionIdx < sessions.length - 1) {
      setSelectedMobileSessionIdx(prev => prev + 1);
    }
  };

  // ============================================================
  // PDF GENERATION - MANUAL TABLE DRAWING (NO autoTable)
  //
  // FIX: jsPDF's doc.text() only ever accepts a string (or an
  // array of strings) — never a raw number. Several cells in the
  // table (the serial number "i + 1", and the summary counters
  // like total/present/absent) were being passed in as numbers,
  // which triggered:
  //   "Type of text must be string or Array. '1' is not recognized."
  // The fix below adds a `toText()` helper that coerces every
  // value to a string right before it reaches doc.text(), and the
  // row-building code now stores those numeric fields as strings
  // too, so this can't regress even if more columns are added later.
  // ============================================================
  const generatePDF = useCallback(async (data) => {
    const { sessions: exportSessions, students, subject, classItem, facultyName, tenantInfo, dateRange, includeSignatures, signatories, generatedAt } = data;
    
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    const addNewPage = () => {
      doc.addPage();
      yPos = margin;
    };

    // Ensures anything sent to doc.text() is a string. Handles
    // numbers, null, and undefined safely.
    const toText = (val) => {
      if (val === null || val === undefined) return '';
      return String(val);
    };

    // Draw header
    if (tenantInfo?.logoUrl) {
      try {
        const response = await fetch(tenantInfo.logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        const imgData = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(imgData, 'JPEG', margin, yPos, 20, 20);
      } catch (e) {
        console.warn('Could not load logo:', e);
      }
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(toText(tenantInfo?.name || 'Institution Name'), pageWidth / 2, yPos + 8, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (tenantInfo?.address) {
      const addressLines = doc.splitTextToSize(toText(tenantInfo.address), pageWidth - margin * 2);
      doc.text(addressLines, pageWidth / 2, yPos + 14, { align: 'center' });
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    let contactText = '';
    if (tenantInfo?.phone) contactText += `Phone: ${tenantInfo.phone}`;
    if (tenantInfo?.email) contactText += contactText ? ` | Email: ${tenantInfo.email}` : `Email: ${tenantInfo.email}`;
    if (contactText) {
      doc.text(toText(contactText), pageWidth / 2, yPos + 19, { align: 'center' });
    }

    yPos += 26;

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ATTENDANCE REPORT', pageWidth / 2, yPos + 5, { align: 'center' });
    yPos += 10;

    // Metadata
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metadata = [
      [`Subject: ${subject?.name || 'N/A'}`, `Class: ${classItem?.className || 'N/A'}`],
      [`Date Range: ${dateRange}`, `Generated: ${moment(generatedAt).format('DD MMM YYYY, hh:mm A')}`],
    ];

    metadata.forEach((row, idx) => {
      const y = yPos + idx * 5;
      doc.text(toText(row[0]), margin, y);
      doc.text(toText(row[1]), pageWidth / 2, y);
    });
    yPos += metadata.length * 5 + 5;

    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // ===== MANUAL TABLE DRAWING =====
    const studentsList = students || [];
    const sessionsList = exportSessions || [];

    // Calculate column widths
    const colWidths = {
      serial: 8,
      name: 35,
      usn: 22,
      session: 11,
      total: 10,
      present: 10,
      absent: 10,
      percent: 12
    };

    const tableWidth = pageWidth - margin * 2;
    
    // Calculate session column width dynamically
    const sessionColWidth = sessionsList.length > 0
      ? (tableWidth - (colWidths.serial + colWidths.name + colWidths.usn + colWidths.total + colWidths.present + colWidths.absent + colWidths.percent)) / sessionsList.length
      : 0;

    const drawTableRow = (rowData, isHeader = false, y) => {
      let x = margin;
      const cellHeight = isHeader ? 8 : 6;
      
      // Serial
      doc.rect(x, y, colWidths.serial, cellHeight);
      doc.setFontSize(isHeader ? 7 : 6);
      doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
      doc.text(toText(rowData[0]), x + colWidths.serial/2, y + 4, { align: 'center' });
      x += colWidths.serial;

      // Name
      doc.rect(x, y, colWidths.name, cellHeight);
      doc.text(toText(rowData[1]), x + 1, y + 4);
      x += colWidths.name;

      // USN
      doc.rect(x, y, colWidths.usn, cellHeight);
      doc.text(toText(rowData[2]), x + 1, y + 4);
      x += colWidths.usn;

      // Sessions
      for (let i = 0; i < sessionsList.length; i++) {
        doc.rect(x, y, sessionColWidth, cellHeight);
        const status = toText(rowData[3 + i]);
        doc.text(status, x + sessionColWidth/2, y + 4, { align: 'center' });
        x += sessionColWidth;
      }

      // Total, Present, Absent, Percent
      const summaryCols = [colWidths.total, colWidths.present, colWidths.absent, colWidths.percent];
      for (let i = 0; i < 4; i++) {
        doc.rect(x, y, summaryCols[i], cellHeight);
        doc.text(toText(rowData[3 + sessionsList.length + i]), x + summaryCols[i]/2, y + 4, { align: 'center' });
        x += summaryCols[i];
      }

      return y + cellHeight;
    };

    // Draw header
    const headerRow = ['#', 'Student Name', 'USN'];
    sessionsList.forEach(s => headerRow.push(formatShortDate(s.sessionDate)));
    headerRow.push('Total', 'Present', 'Absent', '%');

    // Check if we need a new page for headers
    if (yPos + 10 > pageHeight - margin) {
      addNewPage();
    }

    // Draw header with background
    doc.setFillColor(79, 70, 229);
    doc.rect(margin, yPos, tableWidth, 8, 'F');
    yPos = drawTableRow(headerRow, true, yPos);
    yPos += 2;

    // Draw body rows
    for (let i = 0; i < studentsList.length; i++) {
      const student = studentsList[i];
      const studentId = student.studentId || student._id;
      const records = attendanceMap[studentId]?.records || {};
      const summary = attendanceMap[studentId]?.summary || { total: 0, present: 0, absent: 0, effectivePresent: 0, percentage: 0 };

      // All cells stored as strings — drawTableRow feeds these
      // directly into doc.text(), which rejects raw numbers.
      const row = [
        String(i + 1),
        student.name || 'Unknown',
        student.USN || student.rollNumber || 'N/A'
      ];

      sessionsList.forEach(session => {
        const record = records[session._id];
        const status = record?.status || '';
        row.push(getStatusDisplay(status).short);
      });

      row.push(
        String(summary.total || 0),
        String(summary.effectivePresent || 0),
        String(summary.absent || 0),
        `${summary.percentage || 0}%`
      );

      // Check for page break
      if (yPos + 8 > pageHeight - margin) {
        addNewPage();
        // Redraw header on new page
        doc.setFillColor(79, 70, 229);
        doc.rect(margin, yPos, tableWidth, 8, 'F');
        yPos = drawTableRow(headerRow, true, yPos);
        yPos += 2;
      }

      // Alternate row colors
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, yPos, tableWidth, 6, 'F');
      }

      yPos = drawTableRow(row, false, yPos);
    }

    // ===== SUMMARY STATISTICS =====
    yPos += 8;
    const totalStudents = studentsList.length;
    const totalSessionsCount = sessionsList.length;
    const overallAvg = studentsList.length > 0 
      ? Math.round(studentsList.reduce((sum, s) => {
          const sid = s.studentId || s._id;
          return sum + (attendanceMap[sid]?.summary?.percentage || 0);
        }, 0) / studentsList.length)
      : 0;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary Statistics', margin, yPos);
    yPos += 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Students: ${totalStudents}`, margin, yPos);
    doc.text(`Total Sessions: ${totalSessionsCount}`, margin + 50, yPos);
    doc.text(`Overall Attendance: ${overallAvg}%`, margin + 100, yPos);
    yPos += 10;

    // ===== SIGNATURES =====
    if (includeSignatures && signatories && signatories.length > 0) {
      if (yPos + 50 > pageHeight - margin) {
        addNewPage();
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Signatures', margin, yPos);
      yPos += 8;

      let currentSigY = yPos;
      signatories.forEach((sig, idx) => {
        if (currentSigY + 25 > pageHeight - margin) {
          addNewPage();
          currentSigY = margin + 10;
        }

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Signature ${idx + 1}: ${sig.designation || 'Designation'}`, margin, currentSigY);
        doc.line(margin + 5, currentSigY + 10, margin + 80, currentSigY + 10);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(`Name: ${sig.name || '____________________'}`, margin, currentSigY + 18);
        doc.text(`Date: ${sig.date || moment().format('YYYY-MM-DD')}`, margin + 70, currentSigY + 18);
        
        currentSigY += 28;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Generated by ${facultyName || 'Faculty'} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    }

    return doc.output('blob');
  }, [attendanceMap]);

  // ============================================================
  // EXCEL GENERATION
  // ============================================================
  const generateExcel = useCallback((data) => {
    const { sessions: exportSessions, students, subject, classItem, facultyName, tenantInfo, dateRange, includeSignatures, signatories, generatedAt } = data;

    const workbook = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
      ['Attendance Report'],
      [''],
      ['Institution:', tenantInfo?.name || 'Institution Name'],
      ['Address:', tenantInfo?.address || 'N/A'],
      ['Phone:', tenantInfo?.phone || 'N/A'],
      ['Email:', tenantInfo?.email || 'N/A'],
      [''],
      ['Subject:', subject?.name || 'N/A'],
      ['Class:', classItem?.className || 'N/A'],
      ['Faculty:', facultyName || 'N/A'],
      ['Date Range:', dateRange],
      ['Generated:', moment(generatedAt).format('DD MMM YYYY, hh:mm A')],
      [''],
      ['Summary Statistics'],
      ['Total Students:', students?.length || 0],
      ['Total Sessions:', exportSessions?.length || 0],
      ['Overall Attendance:', students?.length > 0 ? 
        Math.round(students.reduce((sum, s) => {
          const sid = s.studentId || s._id;
          return sum + (attendanceMap[sid]?.summary?.percentage || 0);
        }, 0) / students.length) + '%' : '0%'
      ],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Sheet 2: Detailed Attendance
    const studentsList = students || [];
    const sessionsList = exportSessions || [];

    const headers = ['#', 'Student Name', 'USN', 'Roll Number'];
    sessionsList.forEach(s => {
      headers.push(formatShortDate(s.sessionDate));
    });
    headers.push('Total', 'Present', 'Absent', 'Late', 'On Duty', 'Medical', 'Percentage');

    const rows = studentsList.map((student, idx) => {
      const studentId = student.studentId || student._id;
      const records = attendanceMap[studentId]?.records || {};
      const summary = attendanceMap[studentId]?.summary || { 
        total: 0, present: 0, absent: 0, late: 0, onDuty: 0, medical: 0, effectivePresent: 0, percentage: 0 
      };

      const row = [
        idx + 1,
        student.name || 'Unknown',
        student.USN || student.rollNumber || 'N/A',
        student.rollNumber || 'N/A'
      ];

      sessionsList.forEach(session => {
        const record = records[session._id];
        const status = record?.status || '';
        row.push(getStatusDisplay(status).label);
      });

      row.push(
        summary.total || 0,
        summary.effectivePresent || 0,
        summary.absent || 0,
        summary.late || 0,
        summary.onDuty || 0,
        summary.medical || 0,
        `${summary.percentage || 0}%`
      );

      return row;
    });

    const attendanceSheetData = [headers, ...rows];
    const attendanceSheet = XLSX.utils.aoa_to_sheet(attendanceSheetData);
    
    const colWidths = [
      { wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 12 }
    ];
    sessionsList.forEach(() => colWidths.push({ wch: 12 }));
    colWidths.push({ wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 10 });
    attendanceSheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, attendanceSheet, 'Detailed Attendance');

    // Sheet 3: Student-wise Summary
    const studentSummaryData = [
      ['#', 'Student Name', 'USN', 'Total Classes', 'Present', 'Absent', 'Late', 'On Duty', 'Medical', 'Percentage']
    ];

    studentsList.forEach((student, idx) => {
      const studentId = student.studentId || student._id;
      const summary = attendanceMap[studentId]?.summary || { 
        total: 0, present: 0, absent: 0, late: 0, onDuty: 0, medical: 0, effectivePresent: 0, percentage: 0 
      };
      
      studentSummaryData.push([
        idx + 1,
        student.name || 'Unknown',
        student.USN || student.rollNumber || 'N/A',
        summary.total || 0,
        summary.effectivePresent || 0,
        summary.absent || 0,
        summary.late || 0,
        summary.onDuty || 0,
        summary.medical || 0,
        `${summary.percentage || 0}%`
      ]);
    });

    const studentSummarySheet = XLSX.utils.aoa_to_sheet(studentSummaryData);
    studentSummarySheet['!cols'] = [
      { wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, 
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(workbook, studentSummarySheet, 'Student Summary');

    // Sheet 4: Signatures
    if (includeSignatures && signatories && signatories.length > 0) {
      const sigData = [
        ['Signatures'],
        [''],
        ['Designation', 'Name', 'Date']
      ];
      
      signatories.forEach(sig => {
        sigData.push([sig.designation || '', sig.name || '', sig.date || '']);
      });

      const sigSheet = XLSX.utils.aoa_to_sheet(sigData);
      XLSX.utils.book_append_sheet(workbook, sigSheet, 'Signatures');
    }

    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([wbout], { type: 'application/octet-stream' });
  }, [attendanceMap]);

  // Handle download
  const handleDownload = useCallback(async (data) => {
    try {
      let blob;
      let fileName;

      if (data.format === 'pdf') {
        blob = await generatePDF(data);
        fileName = `Attendance_${data.subject?.name || 'Subject'}_${data.classItem?.className || 'Class'}_${moment().format('DD-MM-YYYY')}.pdf`;
      } else {
        blob = generateExcel(data);
        fileName = `Attendance_${data.subject?.name || 'Subject'}_${data.classItem?.className || 'Class'}_${moment().format('DD-MM-YYYY')}.xlsx`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      throw new Error('Failed to generate download');
    }
  }, [generatePDF, generateExcel]);

  if (!loading && !error && sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white h-full border border-slate-200 rounded-xl m-4">
        <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
          <Calendar className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900">No Attendance Ledger Available</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">Active academic logs and generated sheets will propagate into this matrix automatically.</p>
        <button onClick={loadData} className="mt-4 px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-medium tracking-tight transition-all shadow-xs">Initialize View</button>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white h-full border border-slate-200 rounded-xl m-4">
        <AlertCircle className="w-12 h-12 text-rose-500 mb-3" />
        <p className="text-xs font-semibold text-rose-600">{error}</p>
        <button onClick={loadData} className="mt-3 px-4 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-medium">Retry Access</button>
      </div>
    );
  }

  const currentMobileSession = sessions[selectedMobileSessionIdx];

  return (
    <div className="h-full flex flex-col bg-slate-50 text-slate-900 antialiased font-sans">
      
      {/* SaaS Executive Navigation & Workspace Bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3.5 sticky top-0 z-40">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 tracking-wider uppercase">
              <span>Academic Workspace</span>
              <span>/</span>
              <span className={subject?.isLab ? 'text-purple-600' : 'text-indigo-600'}>
                {subject?.isLab ? 'Laboratory Register' : 'Theory Register'}
              </span>
            </div>
            <h1 className="text-base font-bold text-slate-900 tracking-tight mt-0.5">{subject?.name}</h1>
          </div>

          {/* Metric Telemetry Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="bg-slate-50 border border-slate-200/60 rounded-lg px-3 py-1 flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Blocks:</span>
              <span className="text-xs font-bold text-slate-800">{stats.totalSessions} hrs</span>
            </div>
            <div className="bg-slate-50 border border-slate-200/60 rounded-lg px-3 py-1 flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Net Average:</span>
              <span className="text-xs font-bold text-indigo-600">{stats.avg}%</span>
            </div>
            <div className={`border rounded-lg px-3 py-1 flex items-center gap-2 ${stats.below75 > 0 ? 'bg-rose-50/50 border-rose-100 text-rose-700' : 'bg-emerald-50/50 border-emerald-100 text-emerald-700'}`}>
              <span className="text-[10px] font-bold opacity-70 uppercase tracking-wider">Shortage List:</span>
              <span className="text-xs font-bold">{stats.below75} Students</span>
            </div>
          </div>
        </div>

        {/* Real-Time Spreadsheet Toolbar */}
        <div className="flex items-center gap-2 mt-3.5 pt-3 border-t border-slate-100">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter identity, registration code, or full name..."
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-900 focus:bg-white transition-all text-slate-800 placeholder-slate-400"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-1.5 text-xs font-medium border rounded-lg flex items-center gap-1.5 transition-all ${
              showFilters || filterType !== 'all' ? 'bg-slate-900 border-slate-900 text-white shadow-xs' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Refine View</span>
          </button>

          {/* Download Button */}
          <button
            onClick={() => setShowDownloadModal(true)}
            className="px-3 py-1.5 text-xs font-medium border rounded-lg flex items-center gap-1.5 transition-all bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
            disabled={sessions.length === 0 || Object.keys(attendanceMap).length === 0}
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button onClick={loadData} disabled={loading} className="p-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg transition-all">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-indigo-600' : ''}`} />
          </button>
        </div>

        {/* Extended Metric Filters Drawer */}
        {showFilters && (
          <div className="mt-2.5 p-2 bg-slate-50 border border-slate-200 rounded-lg flex flex-wrap gap-1 items-center animate-in slide-in-from-top-1 duration-150">
            {['all', 'critical', 'warning', 'good', 'excellent'].map(filter => (
              <button
                key={filter}
                onClick={() => setFilterType(filter)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-all ${
                  filterType === filter ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {filter === 'all' && 'All Cohorts'}
                {filter === 'critical' && '⚠️ Attendance Shortage (<75%)'}
                {filter === 'warning' && 'Borderline Status (75-85%)'}
                {filter === 'good' && 'Stable (85-95%)'}
                {filter === 'excellent' && 'Excellent (>95%)'}
              </button>
            ))}
            {(searchTerm || filterType !== 'all') && (
              <button
                onClick={() => { setSearchTerm(''); setFilterType('all'); }}
                className="ml-auto px-2 py-1 text-[11px] font-bold text-rose-600 hover:bg-rose-50 rounded-md"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ========================================== */}
      {/* 1. MOBILE CONTEXT-HEADER VIEW PARADIGM    */}
      {/* ========================================== */}
      <div className="flex-1 overflow-y-auto block md:hidden bg-slate-50">
        {loading ? (
          <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="flex flex-col">
            {/* Session Carousel Header Controls */}
            <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-20 shadow-md">
              <button 
                disabled={selectedMobileSessionIdx === 0} 
                onClick={() => handleMobileSessionStep('prev')}
                className="p-1.5 text-slate-400 disabled:opacity-20 active:bg-slate-800 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="text-center">
                <span className="text-[10px] font-bold tracking-wider uppercase text-slate-500 block">Active Timeline Node</span>
                <span className="text-xs font-bold mt-0.5 block">
                  {formatDate(currentMobileSession?.sessionDate)} <span className="text-slate-400 font-normal px-1">•</span> {formatTime(currentMobileSession?.startTime)}
                </span>
              </div>

              <button 
                disabled={selectedMobileSessionIdx === sessions.length - 1} 
                onClick={() => handleMobileSessionStep('next')}
                className="p-1.5 text-slate-400 disabled:opacity-20 active:bg-slate-800 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Adaptive List View */}
            <div className="bg-white border-b border-slate-200 divide-y divide-slate-100 px-4">
              {filteredStudents.map((student, idx) => {
                const record = student.records[currentMobileSession?._id];
                const canEditCell = record?.canEdit === true || canSessionBeMarked(currentMobileSession);
                const pct = student.summary.percentage;
                const pctBadge = pct < 75 ? 'text-rose-700 bg-rose-50' : pct < 85 ? 'text-amber-700 bg-amber-50' : 'text-emerald-700 bg-emerald-50';

                return (
                  <div key={student.details?.studentId || student.details?._id || idx} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-slate-900 truncate tracking-tight">{student.details?.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${pctBadge}`}>{pct}%</span>
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-1 flex items-center gap-1.5">
                        <span className="font-medium tracking-wide">{student.details?.USN || student.details?.rollNumber}</span>
                        <span>•</span>
                        <span>Logs: {student.summary.effectivePresent}/{student.summary.total}</span>
                      </div>
                    </div>

                    <div className="shrink-0">
                      <AttendanceCell
                        status={record?.status}
                        canEdit={canEditCell}
                        sessionId={currentMobileSession?._id}
                        studentId={student.details?.studentId || student.details?._id}
                        onEdit={handleCellEdit}
                        onMarkAttendance={onMarkAttendance}
                        session={currentMobileSession}
                        isMobileView={true}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ========================================== */}
      {/* 2. DESKTOP AIR-GRID LEDGER PARADIGM       */}
      {/* ========================================== */}
      <div className="flex-1 overflow-auto hidden md:block bg-white border-b border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="relative inline-block min-w-full align-middle">
            <table className="min-w-full border-separate border-spacing-0 table-fixed">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th scope="col" className="sticky left-0 top-0 z-30 w-52 bg-slate-50 border-b border-r border-slate-200 px-4 py-3 font-semibold text-slate-500 text-xs tracking-wider uppercase shadow-[1px_0_0_0_#e2e8f0]">
                    Student Roster
                  </th>
                  <th scope="col" className="sticky left-[208px] top-0 z-30 w-16 bg-slate-50 border-b border-r border-slate-200 px-2 py-3 text-center font-semibold text-slate-500 text-xs tracking-wider uppercase shadow-[1px_0_0_0_#e2e8f0]">
                    Agg
                  </th>
                  <th scope="col" className="sticky left-[272px] top-0 z-30 w-16 bg-slate-50 border-b border-r border-slate-200 px-2 py-3 text-center font-semibold text-slate-500 text-xs tracking-wider uppercase shadow-[1px_0_0_0_#e2e8f0]">
                    Net
                  </th>
                  
                  {sessions.map((session) => (
                    <th key={session._id} scope="col" className="top-0 w-11 border-b border-r border-slate-100 bg-slate-50 p-0 text-center font-normal">
                      <div className="flex flex-col items-center justify-center h-12 py-1">
                        <span className="text-[10px] font-bold text-slate-700 block tracking-tight">
                          {formatShortDate(session.sessionDate)}
                        </span>
                        <span className="text-[9px] text-slate-400 font-mono block scale-90 mt-0.5">
                          {formatTime(session.startTime).split(' ')[0]}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-slate-100">
                {filteredStudents.map((student, idx) => {
                  const pct = student.summary.percentage;
                  const pctClass = pct < 75 ? 'text-rose-600 bg-rose-50/30 font-bold' : pct < 85 ? 'text-amber-600 bg-amber-50/30 font-medium' : 'text-emerald-700 bg-emerald-50/30 font-medium';
                  const { imgSrc, initials } = getStudentProfileImage(student.details);

                  return (
                    <tr key={student.details?.studentId || student.details?._id || idx} className="hover:bg-slate-50/40 transition-all group">
                      <td className="sticky left-0 z-20 bg-white group-hover:bg-slate-50 transition-colors border-b border-r border-slate-100 px-4 py-2 flex items-center gap-2.5 h-11 shadow-[1px_0_0_0_#e2e8f0]">
                        <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200">
                          {imgSrc ? (
                            <img src={imgSrc} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[9px] font-bold text-slate-400">{initials}</span>
                          )}
                        </div>
                        <div className="min-w-0 truncate">
                          <div className="text-xs font-bold text-slate-800 truncate tracking-tight">{student.details?.name}</div>
                          <div className="text-[9px] font-mono text-slate-400 truncate mt-0.5">{student.details?.USN || student.details?.rollNumber}</div>
                        </div>
                      </td>

                      <td className={`sticky left-[208px] z-20 text-center border-b border-r border-slate-100 text-xs px-2 h-11 shadow-[1px_0_0_0_#e2e8f0] ${pctClass}`}>
                        {pct}%
                      </td>

                      <td className="sticky left-[272px] z-20 bg-slate-50/50 group-hover:bg-slate-100 transition-colors text-center border-b border-r border-slate-100 text-[10px] font-mono text-slate-500 px-1 h-11 shadow-[1px_0_0_0_#e2e8f0]">
                        {student.summary.effectivePresent}/{student.summary.total}
                      </td>

                      {sessions.map((session) => {
                        const record = student.records[session._id];
                        const canEditCell = record?.canEdit === true || canSessionBeMarked(session);

                        return (
                          <td key={session._id} className="p-0 h-11 border-none">
                            <AttendanceCell
                              status={record?.status}
                              canEdit={canEditCell}
                              sessionId={session._id}
                              studentId={student.details?.studentId || student.details?._id}
                              onEdit={handleCellEdit}
                              onMarkAttendance={onMarkAttendance}
                              session={session}
                              isMobileView={false}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-slate-200 px-4 py-2 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3 text-[11px]">
          <div className="flex items-center gap-3 overflow-x-auto py-0.5">
            <span className="font-bold text-slate-400 text-[9px] uppercase tracking-wider">Legend Map:</span>
            <div className="flex items-center gap-3 font-medium whitespace-nowrap text-slate-600">
              <span className="flex items-center gap-1"><span className="w-4 h-4 text-[10px] font-bold rounded bg-emerald-500/10 text-emerald-700 flex items-center justify-center border border-emerald-500/20">P</span> Present</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 text-[10px] font-bold rounded bg-rose-500/10 text-rose-700 flex items-center justify-center border border-rose-500/20">A</span> Absent</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 text-[10px] font-bold rounded bg-amber-500/10 text-amber-700 flex items-center justify-center border border-amber-500/20">L</span> Late</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 text-[10px] font-bold rounded bg-sky-500/10 text-sky-700 flex items-center justify-center border border-sky-500/20">OD</span> On Duty</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 text-[10px] font-bold rounded bg-purple-500/10 text-purple-700 flex items-center justify-center border border-purple-500/20">M</span> Medical</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400 text-[10px] ml-auto shrink-0">
            <Edit2 className="w-3 h-3 text-slate-400" />
            <span>Select cell directly to update value state</span>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        sessions={sessions}
        students={Object.values(attendanceMap).map(s => s.details)}
        subject={subject}
        classItem={classItem}
        facultyName={facultyId}
        tenantInfo={tenantInfo}
      />
    </div>
  );
};

export default AttendanceMatrixView;